import {
  DataTable,
  DataTableColumn,
  DataTableOptions,
  DataType,
  UserPermission
} from '@globaleris/ui.library'
import {
  Box,
  CardActions,
  CardContent,
  Grid,
  makeStyles,
  Typography,
  useTheme
} from '@material-ui/core'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { ContactInfoWithPrincipalIcon } from '../../../clients.model'
import { styles } from './contactsCard.styles'

import { MailOutline, PersonOutline, PhoneAndroidRounded } from '@material-ui/icons'
import ButtonWithPermissions from '../../../../../components/buttonWithPermissions/buttonWithPermissions'
import { CardWithTransition } from '../../../../../components/shared/cardWithTransition/CardWithTransition'
import { LoadingCard } from '../../../../../components/shared/loadingCard/loadingCard'
import { formatPhoneNumberHelper } from '../../../../../helpers/formatPhoneHelper'
import { getUserPermissions, hasPermission } from '../../../../../helpers/permissionsHelper'
import { Pages, Resources } from '../../../../../model/RBAC/constants'
import { EmptyContent } from '../shared/emptyCard'
import { Header } from '../shared/header'

export interface ContactsCardProps {
  isFetching: boolean
  contacts?: ContactInfoWithPrincipalIcon[]
  error: boolean
  onClickAdd: () => void
  onRetry: () => void
}

export const ContactsCard: React.FC<ContactsCardProps> = ({
  isFetching,
  contacts,
  error,
  onClickAdd,
  onRetry
}) => {
  const [t] = useTranslation(['globalerisLibrary', 'clientFile', 'enums'])

  const hasPermissionClientOrExtendedEdit =
    hasPermission(Resources.Client, [UserPermission.EDIT]) ||
    hasPermission(Resources.ExtendedClients, [UserPermission.EDIT])

  const currentTheme = useTheme()
  const useStyles = makeStyles(styles(currentTheme))
  const classes = useStyles()

  if (isFetching) {
    return <LoadingCard />
  }

  const ContactsHeader = () =>
    React.useMemo(() => {
      return (
        <Header
          avatarIcon={<PersonOutline />}
          title={t('clientFile:components.contactsCard.title')}
          titleVariant="secondary"
        />
      )
    }, [t])

  if (error) {
    return (
      <CardWithTransition show={true} raised>
        <ContactsHeader />
        <EmptyContent title={t('clientFile:components.contactsCard.errorCard.errorMessage')}>
          <ButtonWithPermissions
            hasPermission={true}
            size="small"
            color="primary"
            variant="contained"
            onClick={onRetry}
          >
            {t('clientFile:components.contactsCard.errorCard.buttonLabel')}
          </ButtonWithPermissions>
        </EmptyContent>
      </CardWithTransition>
    )
  }

  if (!contacts || contacts.length === 0) {
    return (
      <CardWithTransition show={true} raised>
        <ContactsHeader />
        <EmptyContent title={t('clientFile:components.contactsCard.emptyCard.title')}>
          <ButtonWithPermissions
            hasPermission={true}
            size="small"
            color="primary"
            variant="contained"
            onClick={onClickAdd}
          >
            {t('clientFile:components.contactsCard.emptyCard.buttonLabel')}
          </ButtonWithPermissions>
        </EmptyContent>
      </CardWithTransition>
    )
  }

  if (contacts.length > 1) {
    const columns: DataTableColumn[] = [
      {
        name: 'principal',
        label: 'Principal',
        type: DataType.BOOLEAN,
        display: false
      },
      {
        name: 'contactName',
        label: t('clientFile:components.contactsCard.dataTable.columns.contactName'),
        type: DataType.TEXT
        // columnWidth: 4
      },
      {
        name: 'email',
        label: t('clientFile:components.contactsCard.dataTable.columns.email'),
        type: DataType.TEXT
        // columnWidth: 5
      },
      {
        name: 'phoneNumber',
        label: t('clientFile:components.contactsCard.dataTable.columns.phoneNumber'),
        type: DataType.TEXT
        // columnWidth: 2
      },
      {
        name: 'actions',
        label: '',
        hideForResponsive: true,
        type: DataType.HTML,
        columnWidth: 1
      }
    ]

    const tableOptions: DataTableOptions = {
      i18n: t('globalerisLibrary:dataTable', { returnObjects: true }),
      elevation: 0,
      download: false,
      print: false,
      selectableRows: false,
      canEdit: false,
      canDelete: false,
      canInsert: false,
      pagination: true,
      rowsPerPage: 5,
      rowsPerPageOptions: [5, 10, 20]
      // canInsert: hasPermission(Resources.Trades, [UserPermission.EDIT]),
      // customInsertEditModal: hasPermission(Resources.Trades, [UserPermission.EDIT]),
      // onRowInsert: async () => {
      //   setShowAddPosition(true)
      //   return true
      // },
      // rowCustomActions: getTableActions()
    }

    const mapContacts = (contacts: ContactInfoWithPrincipalIcon[]) => {
      return contacts.map(contact => ({
        ...contact,
        phoneNumber: formatPhoneNumberHelper(contact.phoneNumber)
      }))
    }

    const tableData = mapContacts(contacts)

    return (
      <CardWithTransition show={true} raised>
        <ContactsHeader />
        <Box m={2} mt={0}>
          <DataTable
            className={classes.dataTableWithoutHeader}
            data={tableData}
            columns={columns}
            options={tableOptions}
            userPermissions={getUserPermissions(Pages.ClientFile)}
          />
        </Box>
        <CardActions className={classes.cardActionsRight}>
          <ButtonWithPermissions
            size="small"
            color="primary"
            hasPermission={hasPermissionClientOrExtendedEdit}
            onClick={onClickAdd}
          >
            {t('clientFile:components.contactsCard.actions.edit')}
          </ButtonWithPermissions>
        </CardActions>
      </CardWithTransition>
    )
  }

  const { contactName, email, phoneNumber } = contacts[0]

  return (
    <CardWithTransition show={true} raised>
      <ContactsHeader />
      <CardContent>
        <Grid container spacing={1} direction="column">
          <Grid item>
            {contactName ? (
              <Typography variant="h2" color="secondary">
                {contactName}
              </Typography>
            ) : (
              ''
            )}
          </Grid>
          {email ? (
            <Grid container item spacing={1} direction="row">
              <Grid item>
                <MailOutline fontSize="small" />
              </Grid>
              <Grid item>
                <Typography variant="body2" className={classes.description}>
                  {email}
                </Typography>
              </Grid>
            </Grid>
          ) : null}
          {phoneNumber ? (
            <Grid container item spacing={1} direction="row">
              <Grid item>
                <PhoneAndroidRounded fontSize="small" />
              </Grid>
              <Grid item>
                <Typography variant="body2" className={classes.description}>
                  {formatPhoneNumberHelper(phoneNumber)}
                </Typography>
              </Grid>
            </Grid>
          ) : null}

          <Grid item></Grid>
        </Grid>
      </CardContent>
      <CardActions className={classes.cardActionsRight}>
        <ButtonWithPermissions
          size="small"
          color="primary"
          hasPermission={hasPermissionClientOrExtendedEdit}
          onClick={onClickAdd}
        >
          {t('clientFile:components.contactsCard.actions.edit')}
        </ButtonWithPermissions>
      </CardActions>
    </CardWithTransition>
  )
}
